import React, { useState, useEffect } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { updateProcurement } from "../../../services/api/procurementService";
import { getUsers } from "../../../services/api/userService";
import StatusBadge from "../../../components/ui/StatusBadge";
import { useUserContext } from "../../../context/userContext";
import Spinner from "../../../components/ui/Spinner";

export default function UsersManagement() {
    const { user } = useUserContext();
    const { procurement } = useOutletContext();
    const { uuidProcurement } = useParams();
    const [users, setUsers] = useState([]);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);
    const [accessMap, setAccessMap] = useState({});
    const [notificationMap, setNotificationMap] = useState({});

    useEffect(() => {
        setIsLoadingUsers(true);
        getUsers()
            .then((allUsers) => {
                const initiatorUuid = procurement.initiator?.uuid;
                const currentUserUuid = user?.uuid;

                const filtered = allUsers
                    .filter((user) => {
                        const groupNames = user.groups?.map(g => g.name);
                        return (
                            groupNames.includes("Tous les utilisateurs")
                            && !groupNames.includes("CA signataires")
                            && user.uuid !== "6a971c38-17cc-4124-b053-a1846ab7d738" // Pierre Colle
                            && user.uuid !== "c4753807-ad7a-4a64-86aa-24129086a0e7" // Cédric
                            && user.uuid !== "a37018ed-3e1b-43ce-922b-98139109abbd" // Didier
                            && user.uuid !== "02f9bb27-62bb-499b-bce7-b9cbab371ce6" // Nicolas
                            && user.uuid !== "696e37c0-1a0f-47d3-8bf6-21c2bdfc8085" // Thomas
                            && user.uuid !== initiatorUuid
                            && user.uuid !== currentUserUuid
                        );
                    })
                    .sort((a, b) => a.fullname.localeCompare(b.fullname));

                setUsers(filtered);

                const initialAccess = {};
                const initialNotif = {};

                filtered.forEach((user) => {
                    const access = getInitialAccess(user);
                    initialAccess[user.uuid] = access;

                    initialNotif[user.uuid] = user.notifiedProcurements?.includes(procurement.uuid) || false;
                });

                setAccessMap(initialAccess);
                setNotificationMap(initialNotif);
            })
            .catch(() => toast.error("Erreur lors du chargement des utilisateurs"))
            .finally(() => setIsLoadingUsers(false));
    // eslint-disable-next-line
    }, []);

    function buildProcurementAccessMap(user) {
        const map = {};
        if (Array.isArray(user.accessibleProcurements)) {
            user.accessibleProcurements.forEach(({ uuid, access }) => {
                map[uuid] = access;
            });
        }
        return map;
    }

    function getInitialAccess(user) {
        const procurementAccess = buildProcurementAccessMap(user);
        const access = procurementAccess[procurement.uuid] || procurementAccess['*'];
        if (access === "write") return "write";
        if (access === "read") return "read";
        return "none";
    }

    async function handleAccessChange(userUuid, newValue) {
        const payloadKey = newValue === "none" ? "removeUsers" : "addUsers";
        const payload = {
            [payloadKey]: [
                {
                    uuidUser: userUuid,
                    ...(newValue !== "none" && {
                        action: newValue === "write" ? "POST" : "GET",
                        beNotified: notificationMap[userUuid] || false,
                    }),
                },
            ],
        };

        await updateProcurement(uuidProcurement, payload)
            .then(() => {
                toast.success("Accès mis à jour avec succès");

                setAccessMap(prev => ({ ...prev, [userUuid]: newValue }));

                // 👇 Si l'accès est mis à "none" et la notif était cochée => on décoche
                if (newValue === "none" && notificationMap[userUuid]) {
                    setNotificationMap(prev => ({ ...prev, [userUuid]: false }));
                }
            })
            .catch(() => {
                toast.error("Erreur lors de la mise à jour de l'accès utilisateur");
            });
    }

    async function handleBeNotifiedChange(userUuid, checked) {
        const currentAccess = accessMap[userUuid];
        if (currentAccess === "none") return;

        const payload = {
            addUsers: [
                {
                    uuidUser: userUuid,
                    action: currentAccess === "write" ? "POST" : "GET",
                    beNotified: checked,
                },
            ],
        };

        await updateProcurement(uuidProcurement, payload)
            .then(() => {
                toast.success("Notification mise à jour");
                setNotificationMap(prev => ({
                    ...prev,
                    [userUuid]: checked,
                }));
            })
            .catch(() => {
                toast.error("Erreur lors de la mise à jour de la notification");
            });
    }

    const getSelectClass = (value) => {
        if (value === "write") return "custom-select write";
        if (value === "read") return "custom-select read";
        return "custom-select none";
    };

    if (isLoadingUsers) {
        return (
            <div className="w-full h-full">
                <Spinner />
            </div>
        );
    }

    if (users.length === 0) {
        return (
            <div className="w-full h-full flex justify-center items-center">
                <StatusBadge text="Aucun utilisateur trouvé." color="gray" />
            </div>
        );
    }

    return (
        <div className="flex flex-col space-y-6 h-full min-h-0">
            <style>
                {`
                    .custom-select {
                        width: 100%;
                        padding: 0.25rem 0.5rem;
                        border-radius: 0.375rem;
                        border: 1px solid #d1d5db;
                        appearance: none;
                    }
                    .custom-select.none {
                        background-color: white;
                        color: #4b5563;
                    }
                    .custom-select.read {
                        background-color: #e0f2fe;
                        color: #1d4ed8;
                    }
                    .custom-select.write {
                        background-color: #dcfce7;
                        color: #15803d;
                    }
                    .custom-select option {
                        background-color: white;
                        color: #9ca3af;
                    }
                    .custom-select option:checked {
                        font-weight: bold;
                    }
                    .custom-select option[value="read"]:checked {
                        background-color: #e0f2fe;
                        color: #1d4ed8;
                    }
                    .custom-select option[value="write"]:checked {
                        background-color: #dcfce7;
                        color: #15803d;
                    }
                    .custom-select option[value="none"]:checked {
                        background-color: white;
                        color: #4b5563;
                    }
                    input[type="checkbox"] {
                        width: 17px;
                        height: 17px;
                        cursor: pointer;
                    }
                `}
            </style>
            <div className="flex-1 min-h-0 overflow-auto">
                <table className="w-full border-separate border-spacing-0">
                    <thead className="sticky top-0">
                        <tr>
                            <th className="border px-2 py-1 text-left bg-white">Utilisateurs</th>
                            <th className="border px-2 py-1 text-center bg-white">Droit d'accès</th>
                            <th className="border px-2 py-1 text-center bg-white">Notification</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((user) => {
                            const access = accessMap[user.uuid] || "none";
                            const beNotified = notificationMap[user.uuid] || false;
                            return (
                                <tr key={user.uuid}>
                                    <td className="border px-2 py-1">{user.fullname}</td>
                                    <td className="border px-2 py-1">
                                        <select
                                            value={access}
                                            onChange={e =>
                                                handleAccessChange(user.uuid, e.target.value)}
                                            className={getSelectClass(access)}
                                        >
                                            <option value="none">Aucun accès</option>
                                            <option value="read">Lecture seule</option>
                                            <option value="write">Écriture</option>
                                        </select>
                                    </td>
                                    <td className="border px-2 py-1 text-center">
                                        <input
                                            type="checkbox"
                                            checked={beNotified}
                                            onChange={e =>
                                                handleBeNotifiedChange(user.uuid, e.target.checked)}
                                            disabled={access === "none"}
                                        />
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
